// Utility functions for API calls with authentication

/**
 * Get authentication headers for API calls
 * @returns {Object} Headers object with Authorization if token exists
 */
export const getAuthHeaders = () => {
  const token = localStorage.getItem('token');
  const headers = {
    'Content-Type': 'application/json',
  };
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  return headers;
};

/**
 * Fetch wrapper that automatically includes auth token
 * @param {string} url - The URL to fetch
 * @param {Object} options - Fetch options
 * @returns {Promise<Response>} Fetch response
 */
export const authFetch = async (url, options = {}) => {
  const token = localStorage.getItem('token');
  const headers = {
    ...options.headers,
  };
  
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  
  return fetch(url, {
    ...options,
    headers,
  });
};

/**
 * Check if user is authenticated
 * @returns {boolean}
 */
export const isAuthenticated = () => {
  return !!localStorage.getItem('token');
};

/**
 * Get current user from localStorage
 * @returns {Object|null}
 */
export const getCurrentUser = () => {
  try {
    const user = localStorage.getItem('user');
    return user ? JSON.parse(user) : null;
  } catch {
    return null;
  }
};

/**
 * Clear authentication data
 */
export const clearAuth = () => {
  localStorage.removeItem('token');
  localStorage.removeItem('user');
  // Purge le branding tenant en cache pour éviter tout résidu visuel au prochain login.
  localStorage.removeItem('agence_branding');
  // Purge le timestamp d'activité partagé entre onglets (nouveau login = nouvelle horloge).
  localStorage.removeItem('komerces_last_activity');
};

/**
 * Révocation réelle de la session côté serveur (best-effort), puis purge locale.
 * Toujours résoudre, même si l'appel réseau échoue.
 */
export const serverLogout = async () => {
  try {
    const token = localStorage.getItem('token');
    if (token) {
      await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/logout`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        keepalive: true,
      });
    }
  } catch (_) {
    /* best-effort : on purge quand même la session locale */
  } finally {
    clearAuth();
  }
};

/**
 * Heartbeat d'activité : informe le serveur que l'utilisateur est actif.
 * - Ne lève JAMAIS d'exception (best-effort).
 * - En cas d'erreur réseau : un retry léger après 3 s.
 * - En cas de 401 : tente le reclaim (stayConnected) une fois.
 * Retourne { ok: boolean, status?: number } pour permettre au caller de logger.
 */
export const sendHeartbeat = async () => {
  const token = localStorage.getItem('token');
  if (!token) return { ok: false, status: 0 };

  const _fire = async () => {
    try {
      const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/heartbeat`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      return { ok: r.ok, status: r.status };
    } catch (err) {
      return { ok: false, status: 0, networkError: true };
    }
  };

  let res = await _fire();
  if (res.networkError) {
    // Retry unique après 3 s (échec réseau ponctuel : WiFi flottant, DNS, etc.)
    await new Promise(r => setTimeout(r, 3000));
    res = await _fire();
    if (!res.ok) console.warn('[HEARTBEAT] échec après retry réseau', res);
  } else if (res.status === 401) {
    // Session expirée (inactivité serveur) → tenter un reclaim silencieux (utilisateur actif si le tab est visible)
    console.warn('[HEARTBEAT] 401 — tentative de reclaim silencieux');
    const claimed = await stayConnected();
    if (claimed.ok) {
      // Reclaim OK → renvoyer un heartbeat "propre"
      res = await _fire();
    } else {
      console.warn('[HEARTBEAT] reclaim échoué', claimed);
    }
  } else if (!res.ok) {
    console.warn('[HEARTBEAT] réponse non-OK', res);
  }
  return res;
};

/**
 * Tentative de « ressusciter » la session côté serveur (bouton "Je suis là" + intercepteur 401).
 * - Auth léniente : JWT signé + non-expiré (24 h) → ré-adopter active_session_id.
 * - 200 : session ranimée (ou déjà OK), les requêtes suivantes passent.
 * - 409 : autre session ouverte ailleurs → PAS de reclaim, message clair à afficher.
 * - 401 : JWT vraiment expiré/invalide → vraie déconnexion.
 * Retourne { ok, status, reason }.
 */
export const stayConnected = async () => {
  try {
    const token = localStorage.getItem('token');
    if (!token) return { ok: false, status: 0, reason: 'no_token' };
    const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/stay-connected`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}` },
    });
    if (r.ok) {
      const data = await r.json().catch(() => ({}));
      return { ok: true, status: 200, reclaimed: !!data.reclaimed };
    }
    if (r.status === 409) {
      const data = await r.json().catch(() => ({}));
      return { ok: false, status: 409, reason: 'session_elsewhere', detail: data?.detail };
    }
    return { ok: false, status: r.status, reason: 'expired' };
  } catch (err) {
    console.warn('[STAY-CONNECTED] erreur réseau', err);
    return { ok: false, status: 0, reason: 'network' };
  }
};
